import React, { useState } from 'react';
import { ApplicationView } from '@aha-app/mvc';
import Tool, {
  type IToolActionCompleteInput,
  type IToolAvailableInput,
  type ToolButtonComponent,
} from './Tool';
import ToolButton from '../toolbars/ToolButton';
import { hasLockBehaviors } from '../objects/guards';
import Shortcut from '../utils/Shortcut';
import { inGroup } from '../utils/objects';

class BaseLockTool extends Tool {
  static toolActionComplete({ controller }: IToolActionCompleteInput) {
    super.toolActionComplete({ controller });
    controller.actionRerenderToolbar();
  }
}

const ToggleLockButton: ToolButtonComponent = ApplicationView(
  ({ controller, selection, tool }) => {
    const [locked, setLocked] = useState(selection?.locked);
    return (
      <ToolButton
        onClick={() => {
          if (hasLockBehaviors(selection)) {
            const tool = locked ? ToolUnlock : ToolLock;
            tool.action({ controller, selection });
            setLocked(!locked);
          }
        }}
        tool={tool}
      >
        <i className={locked ? 'fa-regular fa-lock' : 'fa-regular fa-unlock'} />
      </ToolButton>
    );
  }
);

class ToolLock extends BaseLockTool {
  static toolName = 'Lock';
  static shortcut = new Shortcut('Cmd+Shift+L');

  static action({ controller, selection }) {
    selection.lock?.();
    this.toolActionComplete({ controller });
  }

  static available({ selection }: IToolAvailableInput): boolean {
    return !!selection && !selection.isLocked && !inGroup(selection);
  }

  static enabled({ selection }) {
    return !!selection && !selection.isLocked && !inGroup(selection);
  }

  static buttonComponent() {
    return ToggleLockButton;
  }
}

class ToolUnlock extends BaseLockTool {
  static toolName = 'Unlock';
  static shortcut = new Shortcut('Cmd+Shift+U');

  static action({ controller, selection }) {
    selection.unlock?.();
    this.toolActionComplete({ controller });
  }

  static available({ selection }: IToolAvailableInput): boolean {
    if (!selection) {
      return false;
    }

    return selection.isLocked && !inGroup(selection);
  }

  static enabled({ selection }) {
    return selection?.isLocked && !inGroup(selection);
  }

  static buttonComponent() {
    return ToggleLockButton;
  }
}

class ToolUnlockAll extends BaseLockTool {
  static toolName = 'Unlock all';

  static action({ controller }) {
    controller.canvas
      .getObjects()
      .forEach(obj => hasLockBehaviors(obj) && obj.unlock());
    this.toolActionComplete({ controller });
  }

  static available({ controller }) {
    return controller.canvas.getObjects().some(obj => obj.isLocked);
  }

  static enabled({ controller }) {
    return controller.canvas.getObjects().some(obj => obj.isLocked);
  }
}

export { ToolLock, ToolUnlock, ToolUnlockAll };
